// Feature Flag Utility System — Plutoo Dealer
// Reads from Plutoo Hub Firestore (highest priority) for central feature control

import React from 'react';
import { initializeApp, getApps } from 'firebase/app';
import { getFirestore, doc, getDoc, onSnapshot } from 'firebase/firestore';

// ─── Plutoo Hub connection ─────────────────────────────────────────────────────
const HUB_FIREBASE_CONFIG = {
  apiKey: process.env.REACT_APP_HUB_FIREBASE_API_KEY || 'AIzaSyD3Nn0PRquje1FPdTvQ9-5jULiaN02JEZs',
  authDomain: process.env.REACT_APP_HUB_FIREBASE_AUTH_DOMAIN || 'jupiter-to-plutoo.firebaseapp.com',
  projectId: process.env.REACT_APP_HUB_FIREBASE_PROJECT_ID || 'jupiter-to-plutoo',
  storageBucket: process.env.REACT_APP_HUB_FIREBASE_STORAGE_BUCKET || 'jupiter-to-plutoo.firebasestorage.app',
  messagingSenderId: process.env.REACT_APP_HUB_FIREBASE_MESSAGING_SENDER_ID || '250141547970',
  appId: process.env.REACT_APP_HUB_FIREBASE_APP_ID || '1:250141547970:web:a7bdd7e05f21089f6b0a5b',
};

const HUB_APP_NAME = 'plutoo-hub-readonly';

const getHubDb = () => {
  const existing = getApps().find((a) => a.name === HUB_APP_NAME);
  const hubApp = existing || initializeApp(HUB_FIREBASE_CONFIG, HUB_APP_NAME);
  return getFirestore(hubApp);
};

// The company name is REACT_APP_ACTIVE_COMPANY in Dealer (e.g. 'dealer')
const getCompanyName = () => process.env.REACT_APP_ACTIVE_COMPANY || 'essexautocentre';

// ─── Hub feature cache ─────────────────────────────────────────────────────────
const CACHE_DURATION = 60000; // 1 minute

let hubFeaturesCache = null;
let hubCacheTimestamp = null;
let hubLoadingPromise = null;

export const loadHubFeatureFlags = async () => {
  const now = Date.now();
  if (hubFeaturesCache !== null && hubCacheTimestamp && (now - hubCacheTimestamp) < CACHE_DURATION) {
    return hubFeaturesCache;
  }
  if (hubLoadingPromise) return hubLoadingPromise;

  hubLoadingPromise = (async () => {
    try {
      const hubDb = getHubDb();
      const snap = await getDoc(doc(hubDb, 'companyFeatures', getCompanyName()));
      const features = snap.exists() ? (snap.data().features || null) : null;
      hubFeaturesCache = features;
      hubCacheTimestamp = Date.now();
      return features;
    } catch {
      hubFeaturesCache = null;
      hubCacheTimestamp = Date.now();
      return null;
    } finally {
      hubLoadingPromise = null;
    }
  })();
  return hubLoadingPromise;
};

export const getHubFeatureFlags = () => hubFeaturesCache;

export const clearHubFeatureFlagsCache = () => {
  hubFeaturesCache = null;
  hubCacheTimestamp = null;
  hubLoadingPromise = null;
};

/**
 * Check if a feature is enabled for the current company.
 * Reads from Hub Firestore cache (set via Plutoo Hub Company Management).
 * Falls back to true for unknown features so the app works without Hub config.
 *
 * @param {string} featureName - Feature key (e.g. 'car_sales_module')
 * @param {Object|null} overrideFlags - Optional snapshot of flags (for React hook use)
 * @returns {boolean}
 */
export const isFeatureEnabled = (featureName, overrideFlags = null) => {
  const flags = overrideFlags ?? getHubFeatureFlags();
  if (flags && Object.prototype.hasOwnProperty.call(flags, featureName)) {
    return flags[featureName];
  }
  // No Hub config found — default to enabled so existing functionality is unaffected
  return true;
};

/**
 * React hook that subscribes to Hub feature flags with live updates.
 * Re-renders the component when flags are loaded or change.
 *
 * @param {string} featureName
 * @returns {boolean}
 */
export const useFeatureFlag = (featureName) => {
  const [hubFlags, setHubFlags] = React.useState(() => getHubFeatureFlags());

  React.useEffect(() => {
    // Load initial Hub flags
    loadHubFeatureFlags().then((flags) => {
      setHubFlags(flags);
    }).catch(() => {});

    // Subscribe to Hub feature changes via real-time listener
    try {
      const hubDb = getHubDb();
      const unsubscribe = onSnapshot(
        doc(hubDb, 'companyFeatures', getCompanyName()),
        (snap) => {
          if (snap.exists()) {
            const features = snap.data().features || null;
            hubFeaturesCache = features;
            hubCacheTimestamp = Date.now();
            setHubFlags(features);
          }
        },
        () => {} // Silently fail if Hub unreachable
      );
      return () => unsubscribe();
    } catch {
      return undefined;
    }
  }, []);

  return isFeatureEnabled(featureName, hubFlags);
};

/**
 * Gate component — only renders children if the feature is enabled.
 */
export const FeatureGate = ({ feature, children, fallback = null }) => {
  const isEnabled = useFeatureFlag(feature);
  return isEnabled ? children : fallback;
};

// Initialise Hub cache on module load
if (typeof window !== 'undefined') {
  loadHubFeatureFlags().catch(() => {});
  setInterval(() => loadHubFeatureFlags().catch(() => {}), CACHE_DURATION);
}
